import { useEffect, useState } from "react";
import { Phone, MessageSquare, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { getAircallMeCached, aircallStartCall, aircallSendMessage } from "../../lib/api/aircall";
import { apiErrorText } from "../../lib/api-error-text";

// Click to call / WhatsApp / SMS from the signed-in person's own Aircall
// (Fan, 2026-11-04). Calls ring in their Aircall app from their own line;
// WhatsApp and SMS leave from their Aircall line and land back in BR OS
// through the Aircall webhooks.

const cleanNumber = (n) => String(n || "").replace(/[^\d+]/g, "");

/** Start a call in the person's Aircall app. Returns true when Aircall took it. */
export async function startAircallCall(number, { silent = false } = {}) {
  const me = await getAircallMeCached();
  if (!me?.available) return false;
  try {
    const res = await aircallStartCall({ to: number });
    const d = res.data || {};
    if (d.mode === "dial") {
      toast.success(`Number ready in your Aircall app`, {
        description: "Aircall could not start the call by itself. Press call in the Aircall app.",
      });
    } else {
      toast.success(`Calling ${d.to || number} from Aircall`, {
        description: d.line?.name ? `From ${d.line.name}${d.line.digits ? ` (${d.line.digits})` : ""}` : undefined,
      });
    }
    return true;
  } catch (err) {
    if (!silent) toast.error("Call not started", { description: apiErrorText(err, "Aircall refused the call") });
    return true; // Aircall is set up; do not fall back to another dialler
  }
}

function MessageDialog({ open, onOpenChange, channel, number, name, role, me }) {
  const [body, setBody] = useState("");
  const [lineId, setLineId] = useState("");
  const [sending, setSending] = useState(false);

  const lines = (me?.lines || []).filter((l) => channel !== "whatsapp" || l.whatsapp);
  useEffect(() => {
    if (!open) return;
    const def = channel === "whatsapp"
      ? me?.defaults?.whatsapp?.[role] || me?.defaults?.whatsapp?.client
      : me?.defaults?.sms;
    setLineId(def || lines[0]?.id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channel, role]);

  const label = channel === "whatsapp" ? "WhatsApp" : "SMS";
  const first = (name || "").trim().split(/\s+/)[0] || "there";

  const send = async (confirmSend = false) => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const res = await aircallSendMessage({
        to: number, channel, body, role, recipient_name: name || "",
        line_id: lineId || undefined, confirm_send: confirmSend,
      });
      const d = res.data || {};
      toast.success(`${label} sent from Aircall`, {
        description: d.via === "template"
          ? "No reply from them in the last 24 hours, so it went as the approved template with your text inside."
          : d.line?.name ? `From ${d.line.name}` : undefined,
      });
      setBody("");
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === "object" && detail.code
          && window.confirm(`${detail.message}\n\nSend it anyway?`)) {
        setSending(false);
        await send(true);
        return;
      }
      toast.error(`${label} not sent`, { description: apiErrorText(err, "Aircall refused the message") });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={`aircall-${channel}-dialog`}>
        <DialogHeader>
          <DialogTitle>{label} to {name || number}</DialogTitle>
          <DialogDescription>{number} · sent from your Aircall</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {lines.length > 1 && (
            <label className="block text-xs text-muted-foreground">
              From
              <select
                value={lineId}
                onChange={(e) => setLineId(e.target.value)}
                className="mt-1 block w-full rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                data-testid="aircall-line-select"
              >
                {lines.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}{l.digits ? ` (${l.digits})` : ""}</option>
                ))}
              </select>
            </label>
          )}
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={`Type your ${label} message`}
            rows={5}
            autoFocus
            data-testid="aircall-message-body"
          />
          {channel === "whatsapp" && (
            <p className="text-xs text-muted-foreground">
              If {first} has not messaged us in the last 24 hours, WhatsApp only allows a template. It then goes
              as: "Hi {first}, I hope you're well. Just a quick message, <em>your text</em> Thanks!"
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
          <Button onClick={() => send(false)} disabled={sending || !body.trim() || (channel === "whatsapp" && !lines.length)}>
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Send {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A phone number with Call / WhatsApp / SMS buttons that use the person's Aircall.
 * Without Aircall (no Aircall user for this login) they fall back to tel:, wa.me and sms: links.
 *
 * Props: phone (required), whatsapp (WhatsApp number if different), name, role
 * ("supplier" | "client" | "guest"), showNumber (default true), className.
 */
export default function PhoneActions({ phone, whatsapp, name, role = "client", showNumber = true, className = "", numberTestId }) {
  const [me, setMe] = useState(null);
  const [dialog, setDialog] = useState(null); // "whatsapp" | "sms" | null
  useEffect(() => {
    let alive = true;
    getAircallMeCached().then((m) => { if (alive) setMe(m); });
    return () => { alive = false; };
  }, []);

  const number = cleanNumber(phone);
  if (!number) return null;
  const waNumber = cleanNumber(whatsapp) || number;
  const aircall = me?.available;

  const onCall = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const took = await startAircallCall(number);
    if (!took) window.location.href = `tel:${number}`;
  };
  const onMessage = (channel) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (aircall) { setDialog(channel); return; }
    const digits = (channel === "whatsapp" ? waNumber : number).replace(/^\+/, "");
    window.open(channel === "whatsapp" ? `https://wa.me/${digits}` : `sms:+${digits}`, "_blank", "noopener");
  };
  const via = aircall ? " from your Aircall" : "";
  const btn = "inline-flex items-center justify-center w-6 h-6 rounded hover:bg-muted text-muted-foreground";

  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} data-testid="phone-actions">
      {showNumber && (
        <a href={`tel:${number}`} onClick={onCall} className="text-blue-600 hover:underline mr-1" title={`Call${via}`} data-testid={numberTestId}>
          {phone}
        </a>
      )}
      <button type="button" onClick={onCall} className={`${btn} hover:text-emerald-600`} title={`Call${via}`} aria-label="Call" data-testid="phone-actions-call">
        <Phone className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={onMessage("whatsapp")} className={`${btn} hover:text-green-600`} title={`WhatsApp${via}`} aria-label="WhatsApp" data-testid="phone-actions-whatsapp">
        <MessageCircle className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={onMessage("sms")} className={`${btn} hover:text-blue-600`} title={`SMS${via}`} aria-label="SMS" data-testid="phone-actions-sms">
        <MessageSquare className="w-3.5 h-3.5" />
      </button>
      {aircall && (
        // Portal events bubble through React: keep dialog clicks off parent rows.
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <MessageDialog
          open={!!dialog}
          onOpenChange={(o) => { if (!o) setDialog(null); }}
          channel={dialog || "sms"}
          number={dialog === "whatsapp" ? waNumber : number}
          name={name}
          role={role}
          me={me}
        />
        </span>
      )}
    </span>
  );
}
